import { client } from "../../../api/client";
import { appendRecordings } from "../recordingsSlice";

// Static imports for local recording files so CRA/webpack can resolve them at build time
// Each import resolves to a public URL for the audio file
import Call1 from '../../../assets/local_recordings/Call1_stereo.wav'
import Call2 from '../../../assets/local_recordings/Call2_stereo.wav'
import Call3 from '../../../assets/local_recordings/Call3_stereo.wav'
import Call4 from '../../../assets/local_recordings/Call4_stereo.wav'
import Call5 from '../../../assets/local_recordings/Call5_stereo.wav'
import Call6 from '../../../assets/local_recordings/Call6_stereo.wav'
import oCall1 from '../../../assets/local_recordings/oCall1_stereo.wav'
import oCall2 from '../../../assets/local_recordings/oCall2_stereo.wav'

const queryApiURL = process.env.REACT_APP_QUERY_API_URL;
const recordingApiUrl = process.env.REACT_APP_RECORDING_RETRIEVE_API_URL;
const recordCount = parseInt(process.env.REACT_APP_RECORDCOUNT) || 100;

const localRecordingFiles = {
    'Call1_stereo.wav': Call1,
    'Call2_stereo.wav': Call2,
    'Call3_stereo.wav': Call3,
    'Call4_stereo.wav': Call4,
    'Call5_stereo.wav': Call5,
    'Call6_stereo.wav': Call6,
    'oCall1_stereo.wav': oCall1,
    'oCall2_stereo.wav': oCall2,
};

export const fetchRecordingsData = async (pParams, dispatch) => {
    if (process.env.REACT_APP_USELOCALDATA === "true") {
        const localData = require('./_local/_recordingsData.json');
        let calls = localData.Results || [];

        const term = pParams?.SearchTerm?.trim().toLowerCase();

        // Search-resolved values should belong to the SEARCH OR block, not the filter AND block
        const searchUserIds = pParams?.SearchUserIds || [];
        const searchQueueIds = pParams?.SearchQueueIds || [];
        const searchWrapUpCodes = pParams?.SearchWrapUpCodes || [];

        // Structured filters (AND)
        const filterUserIds = pParams?.UserIds || [];
        const filterQueueIds = pParams?.QueueIds || [];
        const filterWrapUpCodes = (pParams?.WrapUpCodes || []).filter(code => code !== '');

        const contains = (value, search) =>
            String(value || '').toLowerCase().includes(search);

        const hasAny = (arr, selected) =>
            Array.isArray(arr) && arr.some(v => selected.includes(v));

        // 1) Apply structured filters as AND
        calls = calls.filter(c => {
            if (pParams?.ConversationId?.trim()) {
                if (!contains(c.ConverstationID, pParams.ConversationId.trim().toLowerCase())) {
                    return false;
                }
            }

            if (filterUserIds.length > 0) {
                if (!hasAny(c.AgentIDs || [], filterUserIds)) {
                    return false;
                }
            }

            if (filterQueueIds.length > 0) {
                if (!hasAny(c.QueueIds || [], filterQueueIds)) {
                    return false;
                }
            }

            if (filterWrapUpCodes.length > 0) {
                if (!hasAny(c.WrapUpCodes || [], filterWrapUpCodes)) {
                    return false;
                }
            }

            if (pParams?.Contact?.trim()) {
                const contact = pParams.Contact.trim().toLowerCase();
                const matchesContact =
                    contains(c.ANI, contact) ||
                    contains(c.DNIS, contact);
                if (!matchesContact) {
                    return false;
                }
            }

            if (pParams?.Direction?.trim()) {
                if (String(c.Direction || '').toLowerCase() !== pParams.Direction.trim().toLowerCase()) {
                    return false;
                }
            }

            if (pParams?.DateFrom) {
                const from = new Date(pParams.DateFrom);
                if (new Date(c.StartTime) < from) {
                    return false;
                }
            }

            if (pParams?.DateTo) {
                const to = new Date(pParams.DateTo);
                if (new Date(c.StartTime) > to) {
                    return false;
                }
            }

            return true;
        });

        // 2) Apply free-text search as OR
        if (term || searchUserIds.length > 0 || searchQueueIds.length > 0 || searchWrapUpCodes.length > 0) {
            calls = calls.filter(c => {
                const matchesText =
                    (term && (
                        contains(c.ConverstationID, term) ||
                        contains(c.ANI, term) ||
                        contains(c.DNIS, term) ||
                        (Array.isArray(c.AgentNames) && c.AgentNames.some(name => contains(name, term))) ||
                        (Array.isArray(c.QueueNames) && c.QueueNames.some(name => contains(name, term))) ||
                        (Array.isArray(c.WrapUpCodeNames) && c.WrapUpCodeNames.some(name => contains(name, term)))
                    )) || false;

                const matchesResolvedUser = searchUserIds.length > 0 && hasAny(c.AgentIDs || [], searchUserIds);
                const matchesResolvedQueue = searchQueueIds.length > 0 && hasAny(c.QueueIds || [], searchQueueIds);
                const matchesResolvedWrapUp = searchWrapUpCodes.length > 0 && hasAny(c.WrapUpCodes || [], searchWrapUpCodes);

                return matchesText || matchesResolvedUser || matchesResolvedQueue || matchesResolvedWrapUp;
            });
        }

        if (pParams?.SearchId !== undefined) {
            dispatch(appendRecordings({
                results: calls,
                lastkey: {},
                SearchId: pParams.SearchId
            }));
        }

        return {
            Results: calls,
            LastEvaluatedKey: {}
        };
    }

    // existing API path unchanged
    console.log('⚡ fetchRecordingsBatch triggered with:', pParams);

    const { SearchId, mode, append, ...params } = pParams;
    const isInitial = mode === 'initial';
    const isMore = mode === 'more';

    params.Limit = recordCount;

    if (isInitial) {
        let allCalls = [];
        let effectiveLastKey = null;
        let currentParams = { ...params };

        while (allCalls.length < recordCount) {
            const resp = await client.post(queryApiURL, '', currentParams);
            const { Results = [], LastEvaluatedKey } = resp.data;

            if (Results.length > 0) {
                dispatch(appendRecordings({ results: Results, lastkey: LastEvaluatedKey || {}, SearchId }));
                allCalls = allCalls.concat(Results);
            }

            if (LastEvaluatedKey && Object.keys(LastEvaluatedKey).length > 0) {
                currentParams.LastEvaluatedKey = LastEvaluatedKey;
                effectiveLastKey = LastEvaluatedKey;
            } else {
                effectiveLastKey = null;
                break;
            }

            if (allCalls.length >= recordCount) {
                break;
            }
        }

        return {
            Results: [],
            LastEvaluatedKey: effectiveLastKey || {},
            append: false,
            SearchId,
            mode
        };
    } else if (isMore) {
        const resp = await client.post(queryApiURL, '', params);
        const { Results = [], LastEvaluatedKey } = resp.data;

        if (Results.length > 0) {
            dispatch(appendRecordings({ results: Results, lastkey: LastEvaluatedKey || {}, SearchId }));
        }

        return {
            Results,
            LastEvaluatedKey: LastEvaluatedKey || {},
            append: true,
            SearchId,
            mode
        };
    } else {
        throw new Error('Invalid mode');
    }
};

export const fetchRecordingFileData = async (filePath) => {
    if (process.env.REACT_APP_USELOCALDATA === "true") {
        // Look up the local audio file URL from our static imports
        const audioUrl = localRecordingFiles[filePath];
        if (!audioUrl) {
            console.warn(`Local recording file not found: ${filePath}`);
        }
        return audioUrl || '';
    } else {
        let jsonParams = JSON.parse(JSON.stringify({ key: filePath }));
        const response = await client.post(recordingApiUrl, '/api/v1/retrieve', jsonParams);
        return response.data;
    }
};
